import type ApolloCache from '@wora/apollo-cache'
import { getOperationName } from 'apollo-link'
import type { DocumentNode } from 'graphql'
import type { CacheProxy } from '.'

const RE_QUERY_VARIABLES = /(\{.*\})/g

/**
 * Helper function for updating all queries in the cache with any variables. The update function
 * will run for each found query and write it with cache.writeQuery
 **/
export function updateQueries<T>(
  proxy: CacheProxy,
  {
    query,
    update
  }: {
    query: DocumentNode
    update: (data: T, variables?: Record<string, any>) => T
  }
) {
  const cache = proxy as ApolloCache

  // get all queries from cache, pull out the ones that match
  // this will look something like ["$ROOT_QUERY.searchQuotes({\"limit\": 10})"]
  const queryKeys = cache.cache.getAllKeys().filter((key) => key.startsWith(`$ROOT_QUERY.${getOperationName(query)}`))

  // process every matching query
  queryKeys.forEach((key) => {
    // pull out the variables from the query key
    const [vars] = key.match(RE_QUERY_VARIABLES) ?? []

    if (vars) {
      const variables = JSON.parse(vars)
      const data = cache.readQuery<T>({ query, variables })
      cache.writeQuery<T>({
        query,
        variables,
        data: update(data, variables)
      })
    }
  })
}

/**
 * @internal — minimal shape we rely on from `apollo-cache-inmemory`'s current data layer.
 * In production this is either the @wora `Cache` (outside an optimistic transaction) or an
 * `OptimisticCacheLayer` extending `ObjectCache` (inside one). Both expose `delete(key)` —
 * we don't need anything else, and keeping this tight makes a future @wora upgrade easier
 * to audit. Defined here rather than imported to avoid a hard dep on internal types.
 */
interface CacheDataLayer {
  delete(key: string): void
}

/**
 * Removes every cached variant of the given root query from the Apollo cache.
 * After eviction, a cache-first read for that query will miss and trigger a network fetch,
 * which lets `refetchQueries` fully refresh list views that weren't mounted when a mutation fired.
 *
 * IMPORTANT — eviction targets `cache.data` (the *current* layer), not `cache.cache` (the
 * underlying @wora persistent store). Inside an optimistic mutation transaction, Apollo sets
 * `this.data` to a fresh `OptimisticCacheLayer` whose `get(dataId)` falls through to lower
 * layers when the layer's own data has no entry for that key. Mutating only the underlying
 * cache leaves a sibling optimistic layer (e.g. an in-flight soft-delete sitting under the
 * restore mutation we're processing) free to keep serving its filtered list — so the user
 * navigating back to the list during the in-flight window would see stale data until the
 * delete network response landed and Apollo dropped that layer. Evicting on `cache.data` adds
 * the key to the current layer's own data as `undefined`, which masks any lower layer.
 *
 * Outside a transaction `cache.data === cache.cache` (set in `@wora/apollo-cache`), so this
 * keeps the same behavior for the simple "list unmounted, restore completed" case.
 *
 * NOTE: this reaches into the `apollo-cache-inmemory` internal store — the same internal
 * contract already relied on by `updateQueries` above. That store is set up in
 * `apps/shared/graphql/client.ts`; if either dep is upgraded, revisit both helpers together.
 */
export function evictQueries(proxy: CacheProxy, query: DocumentNode) {
  const cache = proxy as ApolloCache
  const opName = getOperationName(query)
  if (!opName) {
    // Anonymous or fragment-only document — nothing safe to filter on. Warn loudly so this
    // surfaces as a misuse during development rather than silently leaving stale list data
    // visible to the user (this code path runs after a destructive mutation succeeded).
    // eslint-disable-next-line no-console
    console.warn('evictQueries: document has no operation name; nothing to evict.')
    return
  }

  // Enumerate the canonical key set from the underlying persistent cache. Optimistic layers
  // only carry keys that have been written to them this transaction, so they can't be the
  // source of truth for "every cached variant" of the query. The `$ROOT_QUERY.` prefix is
  // important: it scopes us to generated dataIds for top-level query field results, not
  // normalized entity keys like `Quote:abc` that share a different namespace.
  const keys = cache.cache.getAllKeys().filter((key) => key.startsWith(`$ROOT_QUERY.${opName}`))

  // The current data layer — equal to `cache.cache` outside a transaction, equal to the active
  // `OptimisticCacheLayer` inside one. Both expose `delete(key)`; on the persistent cache that
  // sets the value to null and pushes the removal to IndexedDB, on the optimistic layer it sets
  // the entry to `undefined` so layer reads stop here instead of falling through.
  //
  // The runtime guard catches the case where a future @wora upgrade re-points or hides this
  // field — without it the eviction would silently fall through to a no-op and the user-visible
  // staleness this fix addresses would quietly come back.
  const data = (cache as unknown as { data?: CacheDataLayer }).data
  if (!data || typeof data.delete !== 'function') {
    // eslint-disable-next-line no-console
    console.warn(
      'evictQueries: cache.data is not a usable layer; the apollo-cache-inmemory / @wora layout may have changed. Eviction skipped.'
    )
    return
  }

  keys.forEach((key) => data.delete(key))
}
